import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { hasPermission } from '../utils/permissions';
import SearchableSelect from '@/components/ui/SearchableSelect';

export default function MasterData() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    parent_category: '', 
    parent_division: '',
    parent_brand: ''
  });
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    if (hasPermission(user, 'master_data', 'view')) {
      fetchData();
    }
  }, [activeTab, user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/master-data/${activeTab}`);
      
      if (activeTab === 'categories') {
        setCategories(res.data || []);
        // Fetch divisions for parent selection
        const divRes = await api.get('/master-data/divisions');
        setDivisions(divRes.data || []);
      } else if (activeTab === 'subcategories') {
        setSubcategories(res.data || []);
        // Also fetch categories for parent selection
        const catRes = await api.get('/master-data/categories');
        setCategories(catRes.data || []);
      } else if (activeTab === 'brands') {
        setBrands(res.data || []);
        // Fetch categories for parent selection
        const catRes = await api.get('/master-data/categories');
        setCategories(catRes.data || []);
      } else if (activeTab === 'divisions') {
        setDivisions(res.data || []);
      } else if (activeTab === 'models') {
        setModels(res.data || []);
        // Fetch brands for parent selection
        const brandRes = await api.get('/master-data/brands');
        setBrands(brandRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/master-data/${activeTab}/${editingItem.name}`, formData);
      } else {
        await api.post(`/master-data/${activeTab}`, formData);
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData({ name: '', description: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      parent_category: item.parent_category || '',
      parent_division: item.parent_division || '',
      parent_brand: item.parent_brand || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (name) => {
    if (!window.confirm(`Delete ${activeTab.slice(0, -1)}?`)) return;
    try {
      await api.delete(`/master-data/${activeTab}/${name}`);
      fetchData();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  const getCurrentData = () => {
    if (activeTab === 'categories') return categories;
    if (activeTab === 'subcategories') return subcategories;
    if (activeTab === 'brands') return brands;
    if (activeTab === 'divisions') return divisions;
    return models;
  };

  const [importing, setImporting] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const res = await api.get('/master-data/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `master_data_export_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/master-data/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'master_data_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Template download failed');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading same file
    if (!file) return;
    if (!window.confirm(`Import master data from "${file.name}"? Existing items (matched by name) will be skipped.`)) return;

    try {
      setImporting(true);
      setImportResult(null);
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/master-data/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const tabLabel = activeTab === 'subcategories' ? 'Sub-Category' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1);

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/dashboard'}
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            ← Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">⚙️ Master Data Settings</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            data-testid="md-template-btn"
          >
            📋 Template
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exportLoading}
            className="text-green-700 border-green-600 hover:bg-green-50"
            data-testid="md-export-btn"
          >
            {exportLoading ? 'Exporting…' : '⬇ Export'}
          </Button>
          <label
            className={`inline-flex items-center px-4 py-2 rounded-md border cursor-pointer ${
              importing
                ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                : 'text-purple-700 border-purple-600 hover:bg-purple-50'
            }`}
            data-testid="md-import-label"
          >
            {importing ? 'Importing…' : '⬆ Import'}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              disabled={importing}
              className="hidden"
              data-testid="md-import-input"
            />
          </label>
          <Button
            onClick={() => {
              setFormData({ name: '', description: '' });
              setEditingItem(null);
              setShowForm(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            + Add {tabLabel}
          </Button>
        </div>
      </div>

      {importResult && (
        <Card className="mb-6 border-blue-300" data-testid="md-import-result">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-gray-900">
                Import complete · {importResult.total_imported} added · {importResult.total_skipped} skipped
              </p>
              <button
                onClick={() => setImportResult(null)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="Dismiss"
              >×</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {Object.entries(importResult.summary || {}).map(([sheet, s]) => (
                <div key={sheet} className="bg-gray-50 rounded p-2 text-center">
                  <div className="font-medium text-gray-800">{sheet}</div>
                  <div className="text-green-700">+{s.imported}</div>
                  <div className="text-gray-500">skipped {s.skipped}</div>
                </div>
              ))}
            </div>
            {importResult.errors?.length > 0 && (
              <details className="mt-3 text-sm">
                <summary className="cursor-pointer text-amber-700">
                  {importResult.errors.length} warning{importResult.errors.length === 1 ? '' : 's'}
                </summary>
                <ul className="list-disc list-inside mt-2 text-gray-700 max-h-40 overflow-auto">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['categories', 'subcategories', 'brands', 'divisions', 'models'].map(tab => (
          <Button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setShowForm(false);
              setFormData({ name: '', description: '', parent_category: '', parent_division: '' });
            }}
            className={activeTab === tab ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}
          >
            {tab === 'subcategories' ? 'Sub-Categories' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Button>
        ))}
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{editingItem ? 'Edit' : 'Add'} {tabLabel}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {activeTab === 'categories' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Division</label>
                  <SearchableSelect
                    value={formData.parent_division}
                    onChange={(val) => setFormData(prev => ({ ...prev, parent_division: val }))}
                    options={divisions.map(div => ({ value: div.name, label: div.name }))}
                    placeholder="Select Division (Optional)"
                  />
                </div>
              )}
              {activeTab === 'subcategories' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category *</label>
                  <SearchableSelect
                    required
                    value={formData.parent_category}
                    onChange={(val) => setFormData(prev => ({ ...prev, parent_category: val }))}
                    options={categories.map(cat => ({ value: cat.name, label: cat.name }))}
                    placeholder="Select Parent Category"
                  />
                </div>
              )}
              {activeTab === 'brands' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
                  <SearchableSelect
                    value={formData.parent_category}
                    onChange={(val) => setFormData(prev => ({ ...prev, parent_category: val }))}
                    options={categories.map(cat => ({ value: cat.name, label: cat.name }))}
                    placeholder="Select Category (Optional)"
                  />
                </div>
              )}
              {activeTab === 'models' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Brand</label>
                  <SearchableSelect
                    value={formData.parent_brand}
                    onChange={(val) => setFormData(prev => ({ ...prev, parent_brand: val }))}
                    options={brands.map(brand => ({ value: brand.name, label: brand.name }))}
                    placeholder="Select Brand (Optional)"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded"
                  required
                  placeholder={`Enter ${tabLabel.toLowerCase()} name`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded"
                  rows="2"
                  placeholder="Optional description"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingItem(null);
                    setFormData({ name: '', description: '' });
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle>All {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} ({getCurrentData().length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : getCurrentData().length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <p className="text-xl font-semibold text-gray-700">No {activeTab} yet</p>
              <p className="text-gray-500">Add your first {tabLabel.toLowerCase()} to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    {activeTab === 'categories' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Division</th>
                    )}
                    {activeTab === 'subcategories' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Category</th>
                    )}
                    {activeTab === 'brands' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Category</th>
                    )}
                    {activeTab === 'models' && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parent Brand</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getCurrentData().map((item) => (
                    <tr key={item.name} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                      {activeTab === 'categories' && (
                        <td className="px-4 py-3 text-sm text-purple-600">{item.parent_division || '-'}</td>
                      )}
                      {activeTab === 'subcategories' && (
                        <td className="px-4 py-3 text-sm text-blue-600">{item.parent_category}</td>
                      )}
                      {activeTab === 'brands' && (
                        <td className="px-4 py-3 text-sm text-green-600">{item.parent_category || '-'}</td>
                      )}
                      {activeTab === 'models' && (
                        <td className="px-4 py-3 text-sm text-orange-600">{item.parent_brand || '-'}</td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-600">{item.description || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(item)}
                            className="text-blue-600 border-blue-600 hover:bg-blue-50"
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(item.name)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
